/**
 * Supabase Auth State Listener
 *
 * Handles automatic session management and state synchronization
 */

import { supabase, convertSupabaseUser } from '../lib/supabase'
import {
  login,
  logout,
  updateSession,
  setLoading,
  setLoadingEssentialData,
  setAppInitialized,
  setUnauthorized
} from './authSlice'
import { clearGlobalConfigsData } from '../../state_manager/state_update_actions/global_configs/index.ts'
import { resetMenuArea } from '../../store/slices/menuAreaSlice.ts'
import { fetchGlobalConfigsThunk, transformLookupsForAuth } from '../../app_modules/global_configs/controller.ts'

/**
 * Check if user's email domain is authorized
 * @param {string} userEmail - User's email address
 * @param {Object} lookupState - Current lookup state from Redux
 * @returns {boolean} - True if authorized, false otherwise
 */
const isEmailDomainAuthorized = (userEmail, lookupState) => {
  if (!userEmail || !lookupState.detailsByCategory['Global Configs']) {
    return false
  }

  // Validate email format using regex to prevent malformed emails
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(userEmail)) {
    return false
  }

  // Extract domain from email - split and take the last part after @
  const emailParts = userEmail.split('@')

  // Email should have exactly one @ symbol (2 parts when split)
  if (emailParts.length !== 2) {
    return false
  }

  const emailDomain = emailParts[1]
  if (!emailDomain) {
    return false
  }

  // Find login_domain lookup detail
  const globalConfigs = lookupState.detailsByCategory['Global Configs']
  const loginDomainConfig = globalConfigs.find((detail) => detail.label === 'login_domain')

  if (!loginDomainConfig || !loginDomainConfig.value) {
    return false
  }

  // Compare domains (case insensitive)
  return emailDomain.toLowerCase() === loginDomainConfig.value.toLowerCase()
}

/**
 * Fetch essential data (global configs) after user login
 * This runs asynchronously and updates the app initialization state
 * Includes guards to prevent unnecessary refetching
 */
const fetchEssentialData = async (dispatch, getState) => {
  // Check if data is already initialized to prevent unnecessary refetching
  const currentState = getState()
  const { appInitialized, isLoadingEssentialData } = currentState.auth

  // Don't fetch if already initialized or currently loading
  if (appInitialized) {
    // Ensure loading state is cleared if app is already initialized
    if (isLoadingEssentialData) {
      dispatch(setLoadingEssentialData(false))
    }
    return
  }

  if (isLoadingEssentialData) {
    return
  }

  try {
    dispatch(setLoadingEssentialData(true))

    // Fetch global configs
    const { user } = currentState.auth
    await dispatch(fetchGlobalConfigsThunk(user))

    // Check domain authorization after loading essential data
    const finalState = getState()
    const { globalConfigs } = finalState

    if (user?.email && globalConfigs?.lookup_headers) {
      const lookupsState = transformLookupsForAuth(globalConfigs.lookup_headers)
      const isAuthorized = isEmailDomainAuthorized(user.email, lookupsState)
      if (!isAuthorized) {
        dispatch(setUnauthorized(true))
        dispatch(setAppInitialized(true)) // Still mark as initialized to prevent loading loops
        return
      }
    }

    // Mark app as initialized if domain check passes
    dispatch(setAppInitialized(true))
  } catch (error) {
    console.error('Error fetching essential data:', error)
    // Mark app as initialized even on error to prevent infinite loading
    dispatch(setAppInitialized(true))
  } finally {
    dispatch(setLoadingEssentialData(false))
  }
}

/**
 * Initialize auth state listener
 * This should be called once when the app starts
 */
export const initAuthListener = (dispatch, getState) => {
  // Listen for authentication state changes

  const {
    data: { subscription }
  } = supabase.auth.onAuthStateChange(async (event, session) => {
    switch (event) {
      case 'INITIAL_SESSION':
        if (session?.user) {
          const user = convertSupabaseUser(session.user)
          dispatch(
            login({
              user,
              session,
              accessToken: session.access_token
            })
          )

          // Fetch essential data asynchronously
          fetchEssentialData(dispatch, getState)
        } else {
          // For INITIAL_SESSION, only set loading to false if no OAuth tokens in URL
          const urlParams = new URLSearchParams(window.location.hash.substring(1))
          const hasTokens = urlParams.get('access_token') || urlParams.get('refresh_token')

          if (!hasTokens) {
            // No session and no tokens in URL, safe to set loading false
            dispatch(setLoading(false))
          }
          // If tokens exist, wait for SIGNED_IN event or session update
        }
        break

      case 'SIGNED_IN':
        if (session?.user) {
          const user = convertSupabaseUser(session.user)
          dispatch(
            login({
              user,
              session,
              accessToken: session.access_token
            })
          )

          // Fetch essential data asynchronously
          fetchEssentialData(dispatch, getState)
        }
        break

      case 'SIGNED_OUT':
        dispatch(logout())
        dispatch(clearGlobalConfigsData())
        dispatch(resetMenuArea())
        dispatch(setUnauthorized(false))
        break

      case 'TOKEN_REFRESHED':
        if (session) {
          dispatch(updateSession(session))
        }
        break

      case 'USER_UPDATED':
        if (session?.user) {
          const user = convertSupabaseUser(session.user)
          dispatch(
            login({
              user,
              session,
              accessToken: session.access_token
            })
          )
        }
        break

      default:
        break
    }
  })

  // Return cleanup function
  return () => {
    subscription.unsubscribe()
  }
}

/**
 * Force refresh essential data
 * This bypasses the initialization check and refetches data
 */
const refreshEssentialData = async (dispatch, getState) => {
  // Reset app initialized state to allow refetching
  dispatch(setAppInitialized(false))

  // Fetch essential data
  await fetchEssentialData(dispatch, getState)
}

/**
 * Check for existing session on app start
 */
export const checkExistingSession = async (dispatch, getState) => {
  try {
    // First check if we have tokens in the URL (OAuth callback)
    const urlParams = new URLSearchParams(window.location.hash.substring(1))
    const accessToken = urlParams.get('access_token')

    if (accessToken) {
      // If we have tokens in URL, give Supabase a moment to process them
      // The auth state listener will handle the session update
      return
    }

    const {
      data: { session },
      error
    } = await supabase.auth.getSession()

    if (error) {
      console.error('Error getting session:', error)
      // Set loading to false even on error
      dispatch(setLoading(false))
      return
    }

    if (session?.user) {
      const user = convertSupabaseUser(session.user)
      dispatch(
        login({
          user,
          session,
          accessToken: session.access_token
        })
      )

      // Fetch essential data asynchronously
      fetchEssentialData(dispatch, getState)
    } else {
      // No session found, set loading to false
      dispatch(setLoading(false))
    }
  } catch (error) {
    console.error('Error checking existing session:', error)
    // Set loading to false even on error
    dispatch(setLoading(false))
  }
}
